import { stateRange, fmtRange } from '../data/hardnessBaseline.js';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '../components/Seo.jsx';
import JsonLd from '../components/JsonLd.jsx';
import {
  classifyHardness,
  mgLToGpg,
  hardnessColor,
  bandsForRange,
  sourceLabel,
  sourceNote,
  ATLAS_PROVENANCE,
  hardnessFigure,
  hardnessSourcing,
  hardnessBandValue,
} from '../data/water.js';
import { WATER_RANKINGS as R } from '../data/waterRankings.js';
import { CITY_CALENDAR } from '../data/cityCalendar.js'; // s135 #52: wash months per city, generated

// ─────────────────────────────────────────────────────────────────────────
// /water/<state> — the state leaf of the Water Atlas. Session 72, P3.
//
// Relationship to /states/<state>: these are different pages answering
// different questions, and the split is deliberate rather than a duplication
// accident.
//
//   /states/<state>  — "how do you clean windows in Ohio?" Climate, seasonal
//                      calendar, housing stock, regional contaminants, an
//                      author's working notes. Water is one section of many.
//   /water/<state>   — "how hard is the water in Ohio, where, and what does
//                      that specific number change?" Every city reading, the
//                      utility behind each, the source type, the method.
//
// Neither is a subset of the other, and each links to the other prominently
// so a reader who wants the other question is one click away. Both carry
// self-referencing canonicals; there is no cross-canonical and no
// duplicate-content relationship — the shared substrate is 3 numbers out of
// a much larger record, rendered for a different purpose with different prose.
//
// Data: loadStateBySlug() from src/data/states/index.js — the same per-state
// micro-chunk StatePage and CityPage already fetch. A visitor to
// /water/ohio downloads ohio.js and nothing else.
// ─────────────────────────────────────────────────────────────────────────

export default function WaterStatePage() {
  const { slug } = useParams();
  const [dark, setDark] = useState(false);
  const [state, setState] = useState(null);
  const [index, setIndex] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | missing

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    import('../data/states/index.js').then((mod) => {
      if (cancelled) return;
      setIndex(mod.STATES_INDEX);
      return mod.loadStateBySlug(slug).then((full) => {
        if (cancelled) return;
        if (!full) { setStatus('missing'); return; }
        setState(full);
        setStatus('ready');
      });
    }).catch(() => { if (!cancelled) setStatus('missing'); });
    return () => { cancelled = true; };
  }, [slug]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  const shell = (children) => (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '32px 24px 80px' }}>
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>
        {children}
      </div>
    </div>
  );

  if (status === 'loading') {
    return shell(
      <p style={{
        fontFamily: fontMono, fontSize: 11, letterSpacing: '0.12em',
        color: T.inkFaint, padding: '40px 0',
      }}>
        LOADING WATER PROFILE…
      </p>
    );
  }

  if (status === 'missing' || !state) {
    return shell(
      <>
        <Seo
          title="Water profile not found"
          description="This state water profile does not exist. The Water Atlas covers all 50 US states — start from the atlas index."
          path={`/water/${slug || ''}`}
          noindex
        />
        <h1 style={{
          fontFamily: fontDisplay, fontSize: 36, fontWeight: 400,
          letterSpacing: '-0.02em', margin: '0 0 12px',
        }}>
          No water profile for that state
        </h1>
        <p style={{ fontFamily: fontBody, fontSize: 15, color: T.inkSoft, marginBottom: 24 }}>
          The Atlas covers all fifty states, so this is almost certainly a typo in the address.
        </p>
        <Link to="/water" style={{
          fontFamily: fontMono, fontSize: 11, letterSpacing: '0.16em',
          color: T.sienna, textDecoration: 'none',
        }}>
          ← THE WATER ATLAS
        </Link>
      </>
    );
  }

  const wp = state.waterProfile || {};
  const cities = Array.isArray(state.topCities) ? state.topCities : [];
  // s135 (DECISIONS #58): the range quoted to search engines is the one the state's covered utilities PUBLISH
  // (waterRankings.js), not the planning waterProfile range (#22). No published figure → no range.
  const pubRows = R.hardness.filter((r) => r.s === state.slug);
  const pubLo = pubRows.length ? Math.min(...pubRows.map((r) => r.lo)) : null;
  const pubHi = pubRows.length ? Math.max(...pubRows.map((r) => r.hi)) : null;
  const pubRange = pubRows.length ? (pubLo === pubHi ? `${pubLo} mg/L` : `${pubLo}–${pubHi} mg/L`) : null;
  const rLo = pubRows.length ? pubLo : wp.hardnessLow, rHi = pubRows.length ? pubHi : wp.hardnessHigh; // headline range
  const spanned = bandsForRange(rLo, rHi);
  const spansMultiple = spanned.length > 1;

  // City readings sorted hardest-first — the useful order for this page,
  // because the hard end is where the method has to change.
  // s111: rank on PUBLISHED figures only. This table used to band, colour, rank
  // and print a grains-per-gallon figure from the planning `hardness` value, so a
  // city whose utility publishes nothing read as, e.g., "VERY HARD · 13.4 gpg".
  // A planning estimate is not a reading: those cities now list after the
  // published ones, alphabetically, with no band and no gpg.
  // Number(null) is 0, so a null band value must be caught before coercion (the
  // first s111 draft printed "SOFT · 0 gpg" for unpublished Laramie; the probe caught it).
  const bandValue = (c) => { const v = hardnessBandValue(c); if (v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
  const publishedCities = cities.filter((c) => bandValue(c) != null).sort((a, b) => bandValue(b) - bandValue(a));
  const otherCities = cities.filter((c) => bandValue(c) == null)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const rankedCities = [...publishedCities, ...otherCities];
  const softestCity = publishedCities[publishedCities.length - 1];
  const hardestCity = publishedCities[0];
  const gpgText = (c) => {
    const h = hardnessSourcing(c);
    if (h?.shape === 'range' && Array.isArray(h.range)) return `${mgLToGpg(h.range[0])}\u2013${mgLToGpg(h.range[1])} gpg`;
    const v = bandValue(c);
    return v == null ? null : `${mgLToGpg(v)} gpg`;
  };

  // Neighbouring-state cards, by region, from the lightweight index.
  const neighbours = (index || [])
    .filter((s) => s.region === state.region && s.slug !== state.slug)
    .slice(0, 4);

  const title = `${state.name} Water Hardness — mg/L by City`;
  const description = pubRange
    ? `Water hardness across ${state.name}: ${pubRange} as published by the utilities of ${pubRows.length} covered ${pubRows.length === 1 ? 'city' : 'cities'}, with utilities and what each level means for cleaning glass.`
    : `Water hardness across ${state.name}: utilities, sources and what each level means for cleaning glass; no covered utility publishes a hardness figure.`;

  const datasetLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: `${state.name} water hardness by city`,
    description:
      `Municipal water hardness (mg/L as CaCO₃) for ${cities.length} cities in ${state.name}, with the serving utility and source type for each. ` +
      (pubRange ? `${pubRows.length} covered ${pubRows.length === 1 ? 'utility publishes' : 'utilities publish'} a figure; published range ${pubRange}.` : 'No covered utility publishes a figure.'),
    url: `https://windowwashingguide.com/water/${state.slug}`,
    isAccessibleForFree: true,
    creator: {
      '@type': 'Organization',
      name: 'Window Washing Guide',
      url: 'https://windowwashingguide.com',
    },
    spatialCoverage: {
      '@type': 'Place',
      name: `${state.name}, United States`,
    },
    temporalCoverage: ATLAS_PROVENANCE.vintage,
    measurementTechnique: ATLAS_PROVENANCE.method,
    isPartOf: {
      '@type': 'Dataset',
      name: 'US Water Hardness Atlas',
      url: 'https://windowwashingguide.com/water',
    },
    variableMeasured: [
      {
        '@type': 'PropertyValue',
        name: 'Water hardness',
        unitText: 'mg/L as CaCO₃',
        ...(pubRows.length ? { minValue: pubLo, maxValue: pubHi } : {}),
      },
    ],
  });

  const breadcrumbLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Guide', item: 'https://windowwashingguide.com/' },
      { '@type': 'ListItem', position: 2, name: 'Water Atlas', item: 'https://windowwashingguide.com/water' },
      { '@type': 'ListItem', position: 3, name: state.name, item: `https://windowwashingguide.com/water/${state.slug}` },
    ],
  });

  return shell(
    <>
      <Seo title={title} description={description} path={`/water/${state.slug}`} ogType="website" />
      <JsonLd payload={datasetLd} />
      <JsonLd payload={breadcrumbLd} />

      <style>{`
        .wwg-city-card { border-top: 1px solid ${T.rule}; }
      `}</style>

      {/* Breadcrumb */}
      <div style={{
        fontFamily: fontMono, fontSize: 11,
        letterSpacing: '0.15em', color: T.inkFaint, marginBottom: 36,
      }}>
        <Link to="/" style={{ color: T.inkFaint, textDecoration: 'none' }}>GUIDE</Link>
        {' / '}
        <Link to="/water" style={{ color: T.inkFaint, textDecoration: 'none' }}>WATER ATLAS</Link>
        {' / '}
        {state.name.toUpperCase()}
      </div>

      <span style={{
        fontFamily: fontMono, fontSize: 11,
        letterSpacing: '0.18em', color: T.sienna,
      }}>
        WATER ATLAS · {state.region.toUpperCase()}
      </span>

      <h1 style={{
        fontFamily: fontDisplay, fontSize: 44, fontWeight: 400,
        lineHeight: 1.05, letterSpacing: '-0.025em', margin: '12px 0 20px',
      }}>
        Water hardness in {state.name}
      </h1>

      {/* At-a-glance figures */}
      <div style={{
        display: 'flex', gap: 28, flexWrap: 'wrap',
        padding: '20px 0', borderTop: `1px solid ${T.rule}`,
        borderBottom: `1px solid ${T.rule}`, marginBottom: 28,
      }}>
        <div>
          <div style={{
            fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
            color: T.inkFaint, marginBottom: 6,
          }}>
            {pubRows.length ? 'PUBLISHED RANGE' : 'PLANNING RANGE'}
          </div>
          <div style={{
            fontFamily: fontMono, fontSize: 20,
            color: hardnessColor(Math.round((rLo + rHi) / 2), T),
          }}>
            {rLo === rHi ? rLo : `${rLo}–${rHi}`}
            <span style={{ fontSize: 12, color: T.inkFaint }}> mg/L</span>
          </div>
          <div style={{ fontFamily: fontMono, fontSize: 11, color: T.inkFaint, marginTop: 3 }}>
            {rLo === rHi ? mgLToGpg(rLo) : `${mgLToGpg(rLo)}–${mgLToGpg(rHi)}`} grains/gal
          </div>
        </div>
        <div>
          <div style={{
            fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
            color: T.inkFaint, marginBottom: 6,
          }}>
            {spansMultiple ? 'BANDS SPANNED' : 'BAND'}
          </div>
          <div style={{
            fontFamily: fontMono, fontSize: 14, fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: '0.04em',
            color: hardnessColor(Math.round((rLo + rHi) / 2), T),
          }}>
            {spanned.length
              ? (spansMultiple
                  ? `${spanned[0].label} → ${spanned[spanned.length - 1].label}`
                  : spanned[0].label)
              : '—'}
          </div>
        </div>
        {wp.dominantSource && (
          <div>
            <div style={{
              fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
              color: T.inkFaint, marginBottom: 6,
            }}>
              DOMINANT SOURCE
            </div>
            <div style={{
              fontFamily: fontMono, fontSize: 14, color: T.ink,
            }}>
              {sourceLabel(wp.dominantSource)}
            </div>
          </div>
        )}
      </div>

      {/* Editorial characterization. dominantTier is PROSE — see src/data/water.js.
          Rendered as the sentence it actually is, never parsed. */}
      {wp.summary && (
        <p style={{
          fontFamily: fontDisplay, fontSize: 20, fontWeight: 300,
          lineHeight: 1.5, color: T.inkSoft, margin: '0 0 20px',
        }}>
          {wp.summary}
        </p>
      )}

      {wp.dominantTier && (
        <p style={{
          fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
          color: T.inkSoft, margin: '0 0 24px',
          paddingLeft: 14, borderLeft: `2px solid ${T.rule}`, maxWidth: 640,
        }}>
          <span style={{
            fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
            color: T.inkFaint, display: 'block', marginBottom: 4,
          }}>
            HOW IT BREAKS DOWN
          </span>
          {wp.dominantTier}
        </p>
      )}

      {wp.notes && (
        <p style={{
          fontFamily: fontBody, fontSize: 15, lineHeight: 1.7,
          color: T.inkSoft, margin: '0 0 20px', maxWidth: 660,
        }}>
          {wp.notes}
        </p>
      )}

      {wp.dominantSource && sourceNote(wp.dominantSource) && (
        <p style={{
          fontFamily: fontBody, fontSize: 15, lineHeight: 1.7,
          color: T.inkSoft, margin: '0 0 36px', maxWidth: 660,
        }}>
          {sourceNote(wp.dominantSource)}
        </p>
      )}

      {/* ── What this means for the glass ───────────────────────────────── */}
      <section style={{ marginBottom: 48 }} id="method">
        <h2 style={{
          fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
          letterSpacing: '-0.02em', margin: '0 0 8px',
        }}>
          What that means for the glass
        </h2>
        <p style={{
          fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
          color: T.inkSoft, margin: '0 0 24px', maxWidth: 620,
        }}>
          {spansMultiple
            ? `${state.name} spans ${spanned.length} hardness bands, which means there is no single answer for the whole state — the method changes as you move across it. Both ends are below.`
            : `${state.name} sits in one band statewide, so the answer is the same everywhere in it.`}
        </p>

        {spanned.map((b, i) => (
          <div key={b.code} style={{ padding: '18px 0', borderTop: `1px solid ${T.rule}` }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', gap: 12, marginBottom: 8, flexWrap: 'wrap',
            }}>
              <h3 style={{
                fontFamily: fontDisplay, fontSize: 20, fontWeight: 400, margin: 0,
              }}>
                {b.label}
                {spansMultiple && (
                  <span style={{
                    fontFamily: fontMono, fontSize: 10, color: T.inkFaint,
                    marginLeft: 10, letterSpacing: '0.1em',
                  }}>
                    {i === 0 ? 'SOFT END' : i === spanned.length - 1 ? 'HARD END' : 'MIDDLE'}
                  </span>
                )}
              </h3>
              <span style={{
                fontFamily: fontMono, fontSize: 12,
                color: hardnessColor(b.min === 0 ? 30 : b.min, T),
              }}>
                {b.max === Infinity ? `${b.min}+` : `${b.min}–${b.max}`} mg/L
              </span>
            </div>
            <p style={{
              fontFamily: fontBody, fontSize: 15, lineHeight: 1.7,
              color: T.inkSoft, margin: 0, maxWidth: 660,
            }}>
              {b.method}
            </p>
          </div>
        ))}
      </section>

      {/* ── City readings ───────────────────────────────────────────────── */}
      {rankedCities.length > 0 && (
        <section style={{ marginBottom: 48 }} id="cities">
          <h2 style={{
            fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
            letterSpacing: '-0.02em', margin: '0 0 8px',
          }}>
            By city
          </h2>
          <p style={{
            fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
            color: T.inkSoft, margin: '0 0 20px', maxWidth: 620,
          }}>
            Hardest first — the order that matters, because the hard end is where the method has to
            change. Each figure is the named utility's own published number; cities whose
            utility publishes none are listed after them, without a band.
            {hardestCity && softestCity && hardestCity !== softestCity && (
              <>
                {' '}Within {state.name} the spread runs from {softestCity.name} at{' '}
                {hardnessFigure(softestCity)} mg/L to {hardestCity.name} at {hardnessFigure(hardestCity)} mg/L — a
                difference big enough that the same bottle of cleaner behaves differently in each.
              </>
            )}
          </p>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto auto',
            gap: 12, padding: '10px 0',
            borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
            fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
            color: T.inkFaint,
          }}>
            <span>CITY / UTILITY</span>
            <span style={{ textAlign: 'right' }}>BAND</span>
            <span style={{ textAlign: 'right' }}>MG/L</span>
          </div>

          {rankedCities.map((c) => {
            const bv = bandValue(c);
            const band = bv == null ? null : classifyHardness(bv);
            const gpg = gpgText(c);
            return (
              <div key={c.name} className="wwg-city-card" style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto',
                gap: 12, padding: '14px 0', alignItems: 'baseline',
              }}>
                <span>
                  <span style={{
                    fontFamily: fontDisplay, fontSize: 17, fontWeight: 400,
                  }}>
                    {c.name}
                  </span>
                  {c.utility && (
                    <span style={{
                      display: 'block', fontFamily: fontBody, fontSize: 12,
                      color: T.inkFaint, marginTop: 2,
                    }}>
                      {c.utility}
                      {c.sourceType && ` · ${sourceLabel(c.sourceType).toLowerCase()}`}
                    </span>
                  )}
                </span>
                <span style={{
                  fontFamily: fontMono, fontSize: 10, letterSpacing: '0.08em',
                  textTransform: 'uppercase', textAlign: 'right',
                  color: bv == null ? T.inkSoft : hardnessColor(bv, T), whiteSpace: 'nowrap',
                }}>
                  {band ? band.label : '—'}
                </span>
                <span style={{
                  fontFamily: fontMono, fontSize: 14, textAlign: 'right',
                  color: bv == null ? T.inkSoft : hardnessColor(bv, T), whiteSpace: 'nowrap',
                }}>
                  {hardnessFigure(c)}
                  {gpg ? (
                    <span style={{
                      display: 'block', fontSize: 10, color: T.inkFaint, marginTop: 2,
                    }}>
                      {gpg}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </section>
      )}

      {/* ── National standing (s134, Phase 5 state roll-up) ──────────────────
          The state's covered cities placed against all 271, from the same
          generated file /water/rankings renders (src/data/waterRankings.js),
          so a rank here and a rank there can never disagree. */}
      {(() => {
        const rows = new Map();
        const put = (r, k, v) => { const key = r.u; if (!rows.has(key)) rows.set(key, { c: r.c, u: r.u, s: r.s }); rows.get(key)[k] = v; };
        R.hardness.filter((r) => r.s === slug).forEach((r) => put(r, 'h', r));
        R.tds.filter((r) => r.s === slug).forEach((r) => put(r, 't', r));
        Object.values(R.hardnessNotRanked).flat().filter((r) => r.s === slug).forEach((r) => put(r, 'nr', true));
        const list = [...rows.values()].sort((a, b) => (a.h ? a.h.rank : 999) - (b.h ? b.h.rank : 999) || a.c.localeCompare(b.c));
        if (!list.length) return null;
        const chl = list.filter((x) => /chloramine|seasonal/.test(R.disinfectantBy[`${slug}/${x.u}`] || '')).length;
        const cell = { padding: '8px 10px 8px 0', borderTop: `1px solid ${T.rule}`, verticalAlign: 'top', fontSize: 14 };
        const th = { ...cell, borderTop: 'none', fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em', color: T.inkFaint, fontWeight: 400, textAlign: 'left' };
        return (
          <section style={{ marginBottom: 48 }} id="national-rank" data-region="state-national-rank">
            <h2 style={{ fontFamily: fontDisplay, fontSize: 28, fontWeight: 400, letterSpacing: '-0.02em', margin: '0 0 8px' }}>
              Against all {R.cities} covered cities
            </h2>
            <p style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.6, color: T.inkSoft, margin: '0 0 16px', maxWidth: 620 }}>
              Where {state.name}'s city pages sit in the <Link to="/water/rankings" style={{ color: T.sienna }}>national city rankings</Link>:
              hardness rank out of {R.hardness.length} cities whose utility publishes a figure, dissolved-solids rank out
              of {R.tds.length}, the disinfectant each utility reports, and the months each city's <Link to="/methodology#calendar" style={{ color: T.sienna }}>cleaning calendar</Link> marks for a wash. Rank 1 is the hardest or most mineral-heavy.
              {chl > 0 ? ` ${chl} of these ${list.length} ${list.length === 1 ? 'city uses' : 'cities use'} chloramine.` : ''}
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', color: T.ink, fontFamily: fontBody }}>
                <thead><tr><th style={th}>CITY</th><th style={th}>HARDNESS RANK</th><th style={th}>TDS RANK</th><th style={th}>DISINFECTANT</th><th style={th}>WASH IN</th></tr></thead>
                <tbody>
                  {list.map((x) => (
                    <tr key={x.u} data-city={`${slug}/${x.u}`}>
                      <td style={cell}><Link to={`/states/${slug}/${x.u}`} style={{ color: T.ink }}>{x.c}</Link></td>
                      <td style={{ ...cell, fontFamily: fontMono, fontSize: 12.5, color: x.h ? hardnessColor(x.h.v, T) : T.inkFaint }}>
                        {x.h ? <>{x.h.rank}<span style={{ color: T.inkFaint }}> / {R.hardness.length}</span><span style={{ display: 'block', color: T.inkFaint, fontSize: 11, whiteSpace: 'nowrap' }}>{x.h.fig} mg/L</span></> : 'not ranked'}
                      </td>
                      <td style={{ ...cell, fontFamily: fontMono, fontSize: 12.5, color: x.t ? T.ink : T.inkFaint }}>
                        {x.t ? <>{x.t.rank}<span style={{ color: T.inkFaint }}> / {R.tds.length}</span><span style={{ display: 'block', color: T.inkFaint, fontSize: 11, whiteSpace: 'nowrap' }}>{x.t.fig} mg/L</span></> : 'not ranked'}
                      </td>
                      <td style={{ ...cell, color: T.inkSoft }}>{R.disinfectantBy[`${slug}/${x.u}`] || '—'}</td>
                      <td data-wash style={{ ...cell, fontFamily: fontMono, fontSize: 12.5, color: CITY_CALENDAR[`${slug}/${x.u}`]?.wash ? T.ink : T.inkFaint, whiteSpace: 'nowrap' }}>{CITY_CALENDAR[`${slug}/${x.u}`]?.wash || 'best months'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })()}

      {/* ── Cross-links ─────────────────────────────────────────────────── */}
      <section style={{
        marginBottom: 40, padding: 20,
        background: T.surface, border: `1px solid ${T.rule}`, borderRadius: 3,
      }}>
        <p style={{
          fontFamily: fontBody, fontSize: 14, lineHeight: 1.65,
          color: T.inkSoft, margin: '0 0 10px',
        }}>
          This page is about {state.name}'s water and nothing else. For how cleaning actually works in{' '}
          {state.name} across the year — climate, seasonal timing, the local contaminants, the housing
          stock — see the{' '}
          <Link to={`/states/${state.slug}`} style={{ color: T.sienna }}>
            {state.name} cleaning guide
          </Link>.
        </p>
        <p style={{
          fontFamily: fontBody, fontSize: 14, lineHeight: 1.65,
          color: T.inkSoft, margin: 0,
        }}>
          For a figure at your own address rather than your city, the{' '}
          <Link to="/tools/hard-water-scorer" style={{ color: T.sienna }}>Hard Water Scorer</Link>{' '}
          takes a ZIP code. If you run a pure-water system, the{' '}
          <Link to="/tools/pure-water-tds-diagnostic" style={{ color: T.sienna }}>TDS diagnostic</Link>{' '}
          reads the other end of the same problem.
        </p>
      </section>

      {/* Neighbours */}
      {neighbours.length > 0 && (
        <section style={{ marginBottom: 40 }}>
          <h2 style={{
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.2em',
            color: T.inkFaint, margin: '0 0 16px', textTransform: 'uppercase',
          }}>
            Nearby in the {state.region}
          </h2>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
          }}>
            {neighbours.map((n) => (
              <Link key={n.slug} to={`/water/${n.slug}`} style={{
                display: 'block', padding: '12px 14px',
                border: `1px solid ${T.rule}`, borderRadius: 2,
                textDecoration: 'none', color: T.ink,
              }}>
                <div style={{ fontFamily: fontDisplay, fontSize: 16 }}>{n.name}</div>
                {(() => { const r = stateRange(n.slug, n.hardnessLow, n.hardnessHigh); return (
                <div style={{
                  fontFamily: fontMono, fontSize: 11, marginTop: 3,
                  color: r.pub ? hardnessColor(r.med, T) : T.inkFaint,
                }}>
                  {fmtRange(r)} mg/L{r.pub ? '' : ' · planning'}
                </div>); })()}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Provenance */}
      <section style={{
        marginBottom: 40, paddingTop: 20, borderTop: `1px solid ${T.rule}`,
      }}>
        <h2 style={{
          fontFamily: fontMono, fontSize: 11, letterSpacing: '0.2em',
          color: T.inkFaint, margin: '0 0 12px', textTransform: 'uppercase',
        }}>
          Where these numbers come from
        </h2>
        <p style={{
          fontFamily: fontBody, fontSize: 13, lineHeight: 1.65,
          color: T.inkSoft, margin: '0 0 8px',
        }}>
          {ATLAS_PROVENANCE.sources.charAt(0).toUpperCase() + ATLAS_PROVENANCE.sources.slice(1)}, {ATLAS_PROVENANCE.vintageLabel}. {ATLAS_PROVENANCE.method}
        </p>
        <p style={{
          fontFamily: fontBody, fontSize: 13, lineHeight: 1.65,
          color: T.inkSoft, margin: 0,
        }}>
          {ATLAS_PROVENANCE.caveat} Have a reading that disagrees with this table?{' '}
          <Link to="/water#submit" style={{ color: T.sienna }}>Send it to us</Link> — a number that
          contradicts the table is worth more than one that confirms it.
        </p>
      </section>

      <footer style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${T.rule}` }}>
        <Link to="/water" style={{
          fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
          color: T.sienna, textDecoration: 'none',
        }}>
          ← THE WATER ATLAS
        </Link>
      </footer>
    </>
  );
}
